/**
 * Dynamic Remote Container Loader
 *
 * Teknik ini load script MFE secara runtime dari URL, tanpa static registration
 * di vite.config.ts federation.remotes.
 *
 * Compatible dengan @originjs/vite-plugin-federation dan Webpack Module Federation.
 *
 * Flow:
 *   URL + Scope + Module
 *     └─► inject <script> ke DOM
 *           └─► akses window[scope]
 *                 └─► init() + get(module)
 *                       └─► factory() → module
 */

const loadedScripts = new Set<string>();

async function loadScript(url: string): Promise<void> {
  if (loadedScripts.has(url)) return;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
      loadedScripts.add(url);
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load remote script: ${url}`));
    document.head.appendChild(script);
  });
}

/**
 * Build shared scope untuk init container.
 *
 * CRITICAL: Inject React & ReactDOM dari host agar remote MFE tidak membuat
 * instance sendiri. Dua instance React berbeda menyebabkan error:
 *   "Cannot read properties of null (reading 'useContext')"
 *
 * Library lain (lucide-react, react-router-dom, dll) juga di-share agar
 * remote tidak perlu load lazy chunks dari servernya sendiri yang
 * bisa saja gagal (chunk not found / CORS).
 *
 * Untuk Webpack host: gunakan __webpack_share_scopes__.default.
 * Untuk Vite host: inject manual ke shared scope sebelum container.init().
 */
async function buildSharedScope(): Promise<Record<string, any>> {
  try {
    // Webpack Module Federation
    if (typeof __webpack_init_sharing__ !== 'undefined') {
      await __webpack_init_sharing__('default');
      return __webpack_share_scopes__.default;
    }
  } catch {
    // Bukan Webpack host, lanjut ke Vite fallback
  }

  // Vite federation: inject shared libraries dari host ke shared scope
  const [React, ReactDOM] = await Promise.all([
    import('react'),
    import('react-dom'),
  ]);

  const sharedScope: Record<string, any> = {};

  /**
   * Buat sebuah "dual factory" yang kompatibel dengan dua skenario Webpack MF runtime:
   *
   * Skenario A (standard): runtime memanggil `factory()` untuk mendapat exports → return ns
   * Skenario B (buggy/older runtime): runtime stores `factory()` ke __webpack_modules__[id]
   *   kemudian memanggil `__webpack_modules__[id](module, exports, require)`.
   *   Jika factory() mengembalikan object (ns), object itu disimpan dan dipanggil sebagai
   *   function → "not a function".
   *
   * Fix: factory SENDIRI bisa dipanggil sebagai module factory Webpack
   * (menerima module, exports, require dan mengisi module.exports).
   * Sekaligus, ketika dipanggil tanpa argumen, mengembalikan ns langsung.
   */
  const createDualFactory = (ns: any): any => {
    const factory: any = function (m?: any, exp?: any) {
      // Dipanggil sebagai Webpack module factory: (module, exports, require)
      if (m && typeof m === 'object' && 'exports' in m) {
        Object.assign(m.exports, ns);
        if ((ns as any).__esModule) m.exports.__esModule = true;
      }
      // Dipanggil sebagai MF factory: () → return exports
      return ns;
    };
    // Copy named exports ke factory agar akses langsung factory.X juga bekerja
    // (compatibility dengan Vite federation yang akses property tanpa memanggil factory)
    try { Object.assign(factory, ns); } catch { /* ESM namespace mungkin non-writable */ }
    return factory;
  };

  // Helper: daftarkan satu library ke shared scope dengan beberapa versi
  const registerShared = (name: string, mod: any, version = '*') => {
    const ns = (mod as any).default !== undefined &&
               typeof (mod as any).default === 'object'
      ? (mod as any).default  // CJS published with default wrapper
      : mod;                   // ESM namespace (lucide-react, zustand, dll)

    const factory = createDualFactory(ns);

    const versions = version === '*'
      ? ['*']
      : Array.from(new Set([version, '18.2.0', '18.0.0']));

    for (const v of versions) {
      sharedScope[name] = sharedScope[name] ?? {};
      sharedScope[name][v] = {
        from: 'host',
        eager: true,
        loaded: 1,
        get: () => Promise.resolve(factory),
      };
    }
  };

  // Wajib: React & ReactDOM (mencegah "Cannot read properties of null (reading 'useContext')")
  const reactVersion = (React as any).default?.version ?? (React as any).version ?? '18.2.0';
  registerShared('react', React, reactVersion);
  registerShared('react-dom', ReactDOM, reactVersion);

  // Optional: share library lain jika tersedia di host
  // Mencegah remote load lazy chunk dari servernya sendiri yang mungkin 404/CORS
  const optionalLibs: { name: string; importer: () => Promise<any> }[] = [
    { name: 'react-router-dom', importer: () => import('react-router-dom') },
    { name: 'lucide-react',     importer: () => import('lucide-react') },
    { name: 'zustand',          importer: () => import('zustand') },
    { name: 'react-router',     importer: () => import('react-router') },
  ];

  await Promise.allSettled(
    optionalLibs.map(async ({ name, importer }) => {
      try {
        const mod = await importer();
        registerShared(name, mod);
      } catch {
        // Library tidak ada di host — biarkan remote load sendiri
      }
    }),
  );

  return sharedScope;
}

export interface RemoteModuleConfig {
  /** URL remoteEntry.js, e.g. "https://mfe.example.com/remoteEntry.js" */
  url: string;
  /** Scope name yang di-expose oleh remote, e.g. "mfe_patient" */
  scope: string;
  /** Module yang di-expose, e.g. "./App" */
  module: string;
}

/**
 * Load modul dari remote container secara dinamis.
 * @returns Modul yang di-export oleh remote (gunakan .default untuk React component)
 */
export async function loadRemoteModule({ url, scope, module }: RemoteModuleConfig): Promise<any> {
  await loadScript(url);

  const container = (window as any)[scope];
  if (!container) {
    throw new Error(`Remote scope "${scope}" not found on window after loading ${url}`);
  }

  const sharedScope = await buildSharedScope();

  // Beberapa federation runtime throw jika init() dipanggil >1x.
  // Guard dengan flag agar tidak double-init, tapi tetap re-init
  // jika called dengan scope baru (misal setelah invalidate).
  try {
    await container.init(sharedScope);
  } catch {
    // Container mungkin sudah di-init sebelumnya — aman diabaikan
  }

  const factory = await container.get(module);
  return factory();
}

/**
 * Invalidasi cache script agar script di-reload saat deploy baru.
 * Gunakan bersama URL versioned: loadRemoteModule({ url: `${baseUrl}?v=${hash}`, ... })
 */
export function invalidateRemoteScript(url: string): void {
  loadedScripts.delete(url);
  // Hapus script tag dari DOM agar dapat di-re-inject
  const existing = document.querySelector(`script[src="${url}"]`);
  existing?.remove();
}
